import { Box, Divider, Grid, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
	DefaultValues,
	FormProvider,
	SubmitHandler,
	useForm,
} from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { FormType } from '@/api/enum/formType.enum';
import {
	Dispatch,
	forwardRef,
	SetStateAction,
	useEffect,
	useImperativeHandle,
} from 'react';
import AppTextField from '@/common/components/form-inputs/AppTextField';
import SingleCheckbox from '@/common/components/form-inputs/SingleCheckbox';
import { eightDigitPhone } from '@/utils/validationHelper';

interface Props {
	type: FormType;
	details?: any;
	setType: Dispatch<SetStateAction<FormType>>;
}

const EditParticularsOfApplicantForm = forwardRef(
	({ type, setType, details }: Props, ref) => {
		const { t } = useTranslation();
		const defaultValues: DefaultValues<any> = {};

		useEffect(() => {
			if (details) methods.reset(details);
		}, [details]);

		const formSchema = Yup.object().shape({
			homeTel: Yup.string()
				.nullable()
				.test(eightDigitPhone(t('errorMessage.phoneMustBeEightDigits'))),
			mobileTel: Yup.string()
				.nullable()
				.test(eightDigitPhone(t('errorMessage.phoneMustBeEightDigits'))),
			caseManagerTel: Yup.string()
				.nullable()
				.test(eightDigitPhone(t('errorMessage.phoneMustBeEightDigits'))),
			othersServiceSpecify: Yup.string().when('othersService', {
				is: (is: boolean) => is,
				then: (schema) => schema.required(t('errorMessage.pleaseEnter')),
				otherwise: (schema) => schema.nullable(),
			}),
			residentialServiceSpecify: Yup.string().when('residentialService', {
				is: (is: boolean) => is,
				then: (schema) => schema.required(t('errorMessage.pleaseEnter')),
				otherwise: (schema) => schema.nullable(),
			}),
			othersRehabilitationSpecify: Yup.string().when('othersRehabilitation', {
				is: (is: boolean) => is,
				then: (schema) => schema.required(t('errorMessage.pleaseEnter')),
				otherwise: (schema) => schema.nullable(),
			}),
		});
		const methods = useForm<any>({
			defaultValues,
			resolver: yupResolver(formSchema),
			mode: 'onChange',
			shouldFocusError: true,
		});

		const watchOthersService = methods.watch('othersService');
		const watchResidentialServiceSpecify = methods.watch('residentialService');
		const watchOthersRehabilitation = methods.watch('othersRehabilitation');

		useImperativeHandle(ref, () => ({
			getFormData: () => methods.getValues(),
			validateForm: () => {
				methods.trigger();
				methods.formState.isValid;
				return methods.trigger().then(() => methods.formState.isValid);
			},
		}));

		return (
			<>
				<FormProvider {...methods}>
					<Typography variant='h6' sx={{ fontWeight: 'bold' }} marginBottom={2}>
						{t('preAdmission.particularsOfApplicant')}
					</Typography>
					<form onSubmit={methods.handleSubmit((e: any) => e.preventDefault())}>
						<Grid container spacing={3} marginTop={0} marginBottom={3}>
							<Grid size={6}>
								<AppTextField
									control={methods.control}
									name='applicantNameEn'
									label={t('preAdmission.name_en')}
								/>
							</Grid>
							<Grid size={6}>
								<AppTextField
									control={methods.control}
									name='applicantNameZh'
									label={t('preAdmission.name_zh')}
								/>
							</Grid>
							<Grid size={6}>
								<AppTextField
									control={methods.control}
									name='homeTel'
									label={t('preAdmission.telNoHome')}
								/>
							</Grid>
							<Grid size={6}>
								<AppTextField
									control={methods.control}
									name='mobileTel'
									label={t('preAdmission.telNoMobile')}
								/>
							</Grid>
							<Grid size={6}>
								<AppTextField
									control={methods.control}
									name='hkicNo'
									label={t('preAdmission.hkicNo')}
								/>
							</Grid>
						</Grid>
						<Divider />
						<Grid container spacing={3} marginTop={3} marginBottom={3}>
							<Grid size={12}>
								<Typography variant='body1'>
									{t('preAdmission.servicesRequiredFromICCMW')}
								</Typography>

								<Grid container spacing={1} marginTop={0}>
									<Grid size={6}>
										<SingleCheckbox
											control={methods.control}
											name='counselling'
											label={t('preAdmission.counselling')}
										/>
									</Grid>
									<Grid size={6}>
										<SingleCheckbox
											control={methods.control}
											name='groupsProgrammes'
											label={t('preAdmission.groupsAndProgrammes')}
										/>
									</Grid>
									<Grid size={6}>
										<SingleCheckbox
											control={methods.control}
											name='skillTraining'
											label={t('preAdmission.skillTraining')}
										/>
									</Grid>
									<Grid size={6}>
										<SingleCheckbox
											control={methods.control}
											name='caseManagement'
											label={t('preAdmission.caseManagement')}
										/>
									</Grid>
									<Grid size={6}>
										<SingleCheckbox
											control={methods.control}
											name='carerSupport'
											label={t('preAdmission.carerSupport')}
										/>
									</Grid>
									<Grid size={6}>
										<SingleCheckbox
											control={methods.control}
											name='peerSupportService'
											label={t('preAdmission.peerSupportService')}
										/>
									</Grid>
									<Grid size={6}>
										<SingleCheckbox
											control={methods.control}
											name='clinicalPsychologicalService'
											label={t('preAdmission.clinicalPsychologicalService')}
										/>
									</Grid>
									<Grid size={6}>
										<SingleCheckbox
											control={methods.control}
											name='childrenServices'
											label={t(
												'preAdmission.servicesForChildrenOfPersonsInMentalRecovery'
											)}
										/>
									</Grid>
									<Grid size={12}>
										<Box display={'flex'}>
											<SingleCheckbox
												sx={{
													flexShrink: 0,
													'& .MuiFormControlLabel-label': {
														whiteSpace: 'nowrap',
														overflow: 'hidden',
														textOverflow: 'ellipsis',
													},
												}}
												control={methods.control}
												name='othersService'
												label={t('preAdmission.others')}
												handleChange={() => {
													methods.setValue('othersServiceSpecify', '');
													methods.clearErrors('othersServiceSpecify');
												}}
											/>
											<AppTextField
												disabled={!watchOthersService}
												sx={{ position: 'relative', top: '-10px' }}
												variantType={'standard'}
												control={methods.control}
												name='othersServiceSpecify'
												label={`${t('preAdmission.othersSpecify')}${
													watchOthersService ? '*' : ''
												}`}
											/>
										</Box>
									</Grid>
									<Grid size={12}>
										<Typography variant='body1'>
											{t(
												'preAdmission.diagnosisSuspectedMentalHealthProblemNoAny'
											)}
										</Typography>
									</Grid>
									<Grid size={12}>
										<SingleCheckbox
											sx={{
												flexShrink: 0,
												'& .MuiFormControlLabel-label': {
													whiteSpace: 'nowrap',
													overflow: 'hidden',
													textOverflow: 'ellipsis',
												},
											}}
											control={methods.control}
											name='diagnosisMentalHealth'
											label={t('preAdmission.diagnosisMentalHealthProblem')}
											handleChange={(e: any) => {
												if (e)
													methods.setValue(
														'suspectedMentalHealthProblem',
														false
													);
											}}
										/>
									</Grid>
									<Grid size={12}>
										<SingleCheckbox
											sx={{
												flexShrink: 0,
												'& .MuiFormControlLabel-label': {
													whiteSpace: 'nowrap',
													overflow: 'hidden',
													textOverflow: 'ellipsis',
												},
											}}
											control={methods.control}
											name='suspectedMentalHealthProblem'
											label={t('preAdmission.suspectedMentalHealthProblem')}
											handleChange={(e: any) => {
												if (e) methods.setValue('diagnosisMentalHealth', false);
											}}
										/>
									</Grid>
									<Grid size={12}>
										<AppTextField
											control={methods.control}
											name='diagnosisMentalHealthSpecify'
											label={`${t('preAdmission.ifAny')}`}
										/>
									</Grid>
								</Grid>
							</Grid>
							<Grid size={6}>
								<AppTextField
									control={methods.control}
									name='dateOfOnset'
									label={t('preAdmission.dateOfOnsetIfAny')}
								/>
							</Grid>
							<Grid size={6}>
								<AppTextField
									control={methods.control}
									name='psychiatricFollowUpClinic'
									label={t('preAdmission.psychiatricFollowUpClinicIfAny')}
								/>
							</Grid>
						</Grid>
						<Divider />
						<Grid container spacing={1} marginTop={3} marginBottom={3}>
							<Grid size={12}>
								<Typography variant='body1'>
									{t('preAdmission.specialRemarks')}
								</Typography>

								<Grid container spacing={1} marginTop={0}>
									<Grid size={6}>
										<SingleCheckbox
											control={methods.control}
											name='conditionalDischarge'
											label={t('preAdmission.conditionalDischarge')}
										/>
									</Grid>
									<Grid size={6}>
										<SingleCheckbox
											control={methods.control}
											name='intensiveCare'
											label={t('preAdmission.intensiveCare')}
										/>
									</Grid>
									<Grid size={6}>
										<SingleCheckbox
											control={methods.control}
											name='exIntensiveCare'
											label={t('preAdmission.exIntensiveCare')}
										/>
									</Grid>
									<Grid size={6}>
										<SingleCheckbox
											control={methods.control}
											name='specialCare'
											label={t('preAdmission.specialCare')}
										/>
									</Grid>
									<Grid size={6}>
										<SingleCheckbox
											control={methods.control}
											name='conventionalCare'
											label={t('preAdmission.conventionalCare')}
										/>
									</Grid>
								</Grid>
							</Grid>
						</Grid>
						<Divider />
						<Grid container spacing={3} marginTop={3} marginBottom={3}>
							<Grid size={12}>
								<Typography variant='body1'>
									{t('preAdmission.contactPointsOfCaseManagerOf')}
								</Typography>

								<Grid container spacing={3} marginTop={0}>
									<Grid size={6}>
										<Box display={'flex'}>
											<SingleCheckbox
												control={methods.control}
												name='cps'
												label={t('preAdmission.cps')}
											/>
											<SingleCheckbox
												control={methods.control}
												name='pcp'
												label={t('preAdmission.pcp')}
											/>
										</Box>
									</Grid>
								</Grid>
							</Grid>
							<Grid size={6}>
								<AppTextField
									control={methods.control}
									name='caseManagerName'
									label={t('preAdmission.name_en')}
								/>
							</Grid>
							<Grid size={6}>
								<AppTextField
									control={methods.control}
									name='caseManagerTel'
									label={t('preAdmission.telNo')}
								/>
							</Grid>
							<Grid size={6}>
								<AppTextField
									control={methods.control}
									name='otherSupportServices'
									label={t('preAdmission.otherSupportServicesEg')}
								/>
							</Grid>
						</Grid>
						<Divider />
						<Grid container spacing={1} marginTop={3} marginBottom={3}>
							<Grid size={12}>
								<Typography variant='body1'>
									{t('preAdmission.detailsOfAnyEmotional')}
								</Typography>
							</Grid>
							<Grid size={12}>
								<SingleCheckbox
									control={methods.control}
									name='suicidalAttempt'
									label={t('preAdmission.suicidalAttempt')}
								/>
							</Grid>
							<Grid size={12}>
								<SingleCheckbox
									control={methods.control}
									name='suicidalTendency'
									label={t('preAdmission.suicidalTendency')}
								/>
							</Grid>
							<Grid size={12}>
								<SingleCheckbox
									control={methods.control}
									name='violence'
									label={t('preAdmission.violence')}
								/>
							</Grid>
							<Grid size={12}>
								<SingleCheckbox
									control={methods.control}
									name='violenceTendency'
									label={t('preAdmission.violenceTendency')}
								/>
							</Grid>
							<Grid size={12}>
								<AppTextField
									control={methods.control}
									name='specialAttentionDetails'
									label={t('preAdmission.ifAny')}
									multiline
									rows={4}
								/>
							</Grid>
						</Grid>
						<Divider />
						<Grid container spacing={1} marginTop={3} marginBottom={3}>
							<Grid size={12}>
								<Typography variant='body1'>
									{t('preAdmission.rehabilitationServicesWaitlisted')}
								</Typography>
							</Grid>
							<Grid size={12}>
								<SingleCheckbox
									control={methods.control}
									name='supportedEmployment'
									label={t('preAdmission.supportedEmployment')}
								/>
							</Grid>
							<Grid size={12}>
								<SingleCheckbox
									control={methods.control}
									name='shelteredWorkshop'
									label={t('preAdmission.shelteredWorkshop')}
								/>
							</Grid>
							<Grid size={12}>
								<Box display={'flex'}>
									<SingleCheckbox
										sx={{
											flexShrink: 0,
											'& .MuiFormControlLabel-label': {
												whiteSpace: 'nowrap',
												overflow: 'hidden',
												textOverflow: 'ellipsis',
											},
										}}
										control={methods.control}
										name='residentialService'
										label={t('preAdmission.residentialServiceSpecify')}
										handleChange={() => {
											methods.setValue('residentialServiceSpecify', '');
											methods.clearErrors('residentialServiceSpecify');
										}}
									/>
									<AppTextField
										disabled={!watchResidentialServiceSpecify}
										sx={{ position: 'relative', top: '-10px' }}
										variantType={'standard'}
										control={methods.control}
										name='residentialServiceSpecify'
										label={`${t('preAdmission.specify')}${
											watchResidentialServiceSpecify ? '*' : ''
										}`}
									/>
								</Box>
							</Grid>
							<Grid size={12}>
								<Box display={'flex'}>
									<SingleCheckbox
										sx={{
											flexShrink: 0,
											'& .MuiFormControlLabel-label': {
												whiteSpace: 'nowrap',
												overflow: 'hidden',
												textOverflow: 'ellipsis',
											},
										}}
										control={methods.control}
										name='othersRehabilitation'
										label={t('preAdmission.othersSpecify')}
										handleChange={() => {
											methods.setValue('othersRehabilitationSpecify', '');
											methods.clearErrors('othersRehabilitationSpecify');
										}}
									/>
									<AppTextField
										disabled={!watchOthersRehabilitation}
										sx={{ position: 'relative', top: '-10px' }}
										variantType={'standard'}
										control={methods.control}
										name='othersRehabilitationSpecify'
										label={`${t('preAdmission.specify')}${
											watchOthersRehabilitation ? '*' : ''
										}`}
									/>
								</Box>
							</Grid>
							<Grid size={12}>
								<SingleCheckbox
									control={methods.control}
									name='notKnown'
									label={t('preAdmission.notKnown')}
								/>
							</Grid>
						</Grid>
						<Divider />
						<Grid container spacing={3} marginTop={3} marginBottom={3}>
							<Grid size={12}>
								<Typography variant='body1'>
									{t('preAdmission.consentOfApplicant')}
								</Typography>
							</Grid>
							<Grid size={12}>
								<Typography variant='body2' color='common.neutral40'>
									{t('preAdmission.forReceivingICCMWServices')}
								</Typography>
								<SingleCheckbox
									control={methods.control}
									name='receivingObtained'
									label={t('preAdmission.hasBeenObtained')}
									handleChange={(checked: boolean) => {
										if (checked) methods.setValue('receivingNotObtained', false);
									}}
								/>
								<Box>
									<SingleCheckbox
										control={methods.control}
										name='receivingNotObtained'
										label={t('preAdmission.hasNotBeenObtained')}
										handleChange={(checked: boolean) => {
											if (checked) methods.setValue('receivingObtained', false);
										}}
									/>
								</Box>
							</Grid>
							<Grid size={12}>
								<Typography variant='body2' color='common.neutral40'>
									{t('preAdmission.workerCanApproachCaseMedicalOfficer')}
								</Typography>
								<SingleCheckbox
									control={methods.control}
									name='approachObtained'
									label={t('preAdmission.hasBeenObtained')}
									handleChange={(checked: boolean) => {
										if (checked) methods.setValue('approachNotObtained', false);
									}}
								/>
								<Box>
									<SingleCheckbox
										control={methods.control}
										name='approachNotObtained'
										label={t('preAdmission.hasNotBeenObtained')}
										handleChange={(checked: boolean) => {
											if (checked) methods.setValue('approachObtained', false);
										}}
									/>
								</Box>
							</Grid>
						</Grid>
					</form>
				</FormProvider>
			</>
		);
	}
);

EditParticularsOfApplicantForm.displayName = 'EditParticularsOfApplicantForm';

export default EditParticularsOfApplicantForm;
